/**
 * api.js
 *
 * Tous les endpoints API
 */

import axios from "axios";

//const API_BASE = "http://127.0.0.1:5000";
const API_BASE = "https://altered-checker.online/api"; // ton backend Flask

/**
 * Instance axios avec configuration
 */
export const api = axios.create({
    baseURL: API_BASE,
    withCredentials: true,
});

/**
 * Définit le token d'authentification
 */
export const setAuthToken = (token) => {
    api.defaults.headers.common["Authorization"] = token ? `Bearer ${token}` : "";
};

// ============================================================
// CARTES
// ============================================================

/**
 * Recherche avancée de cartes
 */
export const searchCards = (filters, page = 1, perPage = 20, sortBy = "date", order = "desc") => {
    return api.get("/cards/search", {
        params: { ...filters, page, per_page: perPage, sort_by: sortBy, order },
    });
};

/**
 * Détail d'une carte
 */
export const getCardById = (cardId) => {
    return api.get(`/cards/${cardId}`);
};

// ============================================================
// FACTIONS
// ============================================================

/**
 * Liste des factions
 */
export const getFactions = () => {
    return axios.get(`${API_BASE}/factions`);
};

// ============================================================
// AUTHENTIFICATION
// ============================================================

/**
 * Connexion
 */
export const loginUser = (login, password) => {
    return api.post("/login", { login, password });
};

/**
 * Inscription
 */
export const registerUser = (login, password) => {
    return api.post("/register", { login, password });
};

// ============================================================
// FAVORIS
// ============================================================

/**
 * Liste des favoris
 */
export const getFavorites = () => {
    return api.get("/favorites");
};

/**
 * Ajouter aux favoris
 */
export const addFavorite = (card_id) => {
    return api.post("/favorites/add", { card_id });
};

/**
 * Retirer des favoris
 */
export const removeFavorite = (card_id) => {
    return api.post("/favorites/remove", { card_id });
};

// ============================================================
// RECHERCHES SAUVEGARDÉES
// ============================================================

/**
 * Sauvegarder une recherche
 */
export const saveSearch = (name, filters) => {
    return api.post("/saved-searches", { name, filters });
};

/**
 * Récupérer les recherches sauvegardées
 */
export const getSavedSearches = () => {
    return api.get("/saved-searches");
};

/**
 * Mettre à jour une recherche (name, filters, is_active)
 */
export const updateSavedSearch = (id, data) => {
    return api.put(`/saved-searches/${id}`, data);
};

/**
 * Supprimer une recherche
 */
export const deleteSavedSearch = (id) => {
    return api.delete(`/saved-searches/${id}`);
};

// ============================================================
// ALERTES (NOUVEAU)
// ============================================================

/**
 * Récupérer les alertes d'une recherche (NOUVEAU)
 */
export const getSearchAlerts = (searchId, params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append("page", params.page);
    if (params.per_page) queryParams.append("per_page", params.per_page);
    if (params.change_type) queryParams.append("change_type", params.change_type);

    const url = `/saved-searches/${searchId}/alerts?${queryParams}`;
    return api.get(url);
};

/**
 * Marquer une alerte comme lue (NOUVEAU)
 */
export const markAlertAsRead = (alertId) => {
    return api.post(`/alerts/${alertId}/mark-read`);
};

/**
 * Récupérer toutes les alertes non lues (NOUVEAU)
 */
export const getUnreadAlerts = () => {
    return api.get("/alerts/unread");
};


// ============================================================================
// SETTINGS UTILISATEUR
// ============================================================================

/**
 * Récupérer tous les settings utilisateur
 * @returns {Promise<{id, login, discord_user_id, created_at}>}
 */
export const getUserSettings = async () => {
    try {
        const response = await api.get("/user/settings");
        return response.data;
    } catch (error) {
        console.error("❌ Erreur chargement settings:", error);
        throw error;
    }
};

/**
 * Mettre à jour les settings utilisateur
 * @param {Object} settings - { login?, password?, password_confirm?, discord_user_id? }
 * @returns {Promise<{id, login, discord_user_id, message}>}
 */
export const updateUserSettings = async (settings) => {
    try {
        // Valide que les données existent
        if (!settings || Object.keys(settings).length === 0) {
            throw new Error("Aucun paramètre à mettre à jour");
        }

        const response = await api.put("/user/settings", settings);
        return response.data;
    } catch (error) {
        console.error("❌ Erreur mise à jour settings:", error);
        throw error;
    }
};

/**
 * Changer le username
 * @param {string} newLogin - Nouveau login
 * @returns {Promise<{...settings}>}
 */
export const changeUsername = async (newLogin) => {
    if (!newLogin || newLogin.trim().length < 3) {
        throw new Error("Login doit avoir au moins 3 caractères");
    }

    return updateUserSettings({ login: newLogin.trim() });
};

/**
 * Changer le password
 * @param {string} newPassword - Nouveau mot de passe
 * @param {string} confirmPassword - Confirmation
 * @returns {Promise<{...settings}>}
 */
export const changePassword = async (newPassword, confirmPassword) => {
    if (!newPassword || newPassword.length < 6) {
        throw new Error("Mot de passe doit avoir au moins 6 caractères");
    }

    if (newPassword !== confirmPassword) {
        throw new Error("Les mots de passe ne correspondent pas");
    }

    return updateUserSettings({
        password: newPassword,
        password_confirm: confirmPassword,
    });
};

/**
 * Configurer Discord ID manuellement
 * @param {string} discordUserId - Discord User ID
 * @returns {Promise<{...settings}>}
 */
export const setDiscordUserId = async (discordUserId) => {
    if (!discordUserId || !String(discordUserId).match(/^\d+$/)) {
        throw new Error("Discord ID doit être un nombre");
    }

    return updateUserSettings({ discord_user_id: discordUserId });
};

/**
 * Supprimer la connexion Discord
 * @returns {Promise<{...settings}>}
 */
export const removeDiscordUserId = async () => {
    return updateUserSettings({ discord_user_id: "" });
};

// ============================================================================
// DISCORD OAUTH
// ============================================================================

/**
 * Obtenir l'URL d'autorisation Discord
 * @returns {Promise<{authorization_url: string}>}
 */
export const getDiscordAuthorizationUrl = async () => {
    try {
        const response = await api.post("/auth/discord/authorize", {});
        return response.data;
    } catch (error) {
        console.error("❌ Erreur Discord authorization:", error);
        throw error;
    }
};

/**
 * Vérifier que Discord est connecté
 * @param {string} discordUserId
 * @returns {Promise<{verified: boolean, message: string}>}
 */
export const verifyDiscordConnection = async (discordUserId) => {
    try {
        const response = await api.post("/auth/discord/verify", {
            discord_user_id: discordUserId,
        });
        return response.data;
    } catch (error) {
        console.error("❌ Erreur vérification Discord:", error);
        throw error;
    }
};

// ============================================================================
// SUPPRESSION DE COMPTE
// ============================================================================

/**
 * Supprimer le compte utilisateur
 * @returns {Promise<{message: string}>}
 */
export const deleteUserAccount = async () => {
    try {
        const response = await api.delete("/user/account");
        return response.data;
    } catch (error) {
        console.error("❌ Erreur suppression compte:", error);
        throw error;
    }
};

// ============================================================
// SETS
// ============================================================

/**
 * Récupérer la liste des sets
 */
export const getSets = () => {
    return api.get("/sets");
};

export const getEffects = () => api.get("/effects");
